import React, { useContext, useEffect, useState } from 'react'
import { Button, makeStyles, shorthands, tokens } from '@fluentui/react-components'
import { AddCircle24Regular } from '@fluentui/react-icons'
import { RiskAnalysis, RiskAnalysisStatus, RiskManagementModules } from '../../../api/schemas/schema'
import { ModalRisk } from './ModalRisk'
import { Text } from '../../common/text/Text'
import { t } from '../../../i18n/i18n'
import { getSimplifiedRiskIdentifyingColumns } from '../../../helpers/riskAnalysisHelper'
import { List } from '../../common/list/List'
import { InfoMessageBar } from '../../common/other/InfoMessageBar'
import { CardHeader } from '../../common/header/CardHeader'
import { IItemState } from '../../../interfaces/IItemState'
import { DataContext } from '../../../context/DataContext'
import { ModalWarningNoProtectedAssetGoal } from './ModalWarningNoProtectedAssetGoal'
import { areModulesActiveInOrganisation } from '../../../helpers/organisationHelper'
import { ModuleGuard } from '../../common/other/ModuleGuard'

const useStyles = makeStyles({
   btn: {
      '@media screen and (max-width: 1007px)': {
         width: '100%',
      },
   },
   hideInPhone: {
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   showOnlyPhone: {
      display: 'none',
      '@media screen and (max-width: 1007px)': {
         display: 'block',
      },
   },
   text: {
      ...shorthands.margin(tokens.spacingHorizontalS, 0),
   },
})

type RiskIdentifyingProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   analysisState: IItemState<RiskAnalysis>
}

export const RiskIdentifying = ({ setRiskAnalysisState, analysisState }: RiskIdentifyingProps) => {
   const dataContext = useContext(DataContext)
   const { protectedAssetGoals } = dataContext.state
   const { organisation } = dataContext.state
   const classes = useStyles()
   const [openModal, setOpenModal] = useState<boolean>(false)
   const [openWarningModal, setOpenWarningModal] = useState<boolean>(false)
   const [selectedItem, setSelectedItem] = useState<any>(null)
   const [risks, setRisks] = useState<any>([])
   const [lastUsedAssetGoalId, setLastUsedAssetGoalId] = useState<string | null>(null)
   const [lastUsedThreatId, setLastUsedThreatId] = useState<string | null>(null)
   const analysis = analysisState.item
   const disabled = analysisState.disabled || analysis.status === RiskAnalysisStatus.Completed

   const handleRowClick = (item: any) => {
      setSelectedItem(item)
      setOpenModal(true)
   }

   const handleNewClick = () => {
      if (
         areModulesActiveInOrganisation(organisation, [
            RiskManagementModules.SIMPLIFIED_RISK_ANALYSIS,
            RiskManagementModules.CONSEQUENCE_AND_PROBABILITY_ANALYSIS,
         ]) &&
         protectedAssetGoals.length === 0
      ) {
         setOpenWarningModal(true)
      } else {
         setSelectedItem(null)
         setOpenModal(true)
      }
   }

   const handleCloseWarningModal = () => {
      setOpenWarningModal(false)
      setSelectedItem(null)
      setOpenModal(true)
   }

   const phoneBody = (risk: any) => {
      return (
         <>
            <div>
               <Text size={400} className={classes.text}>
                  {risk?.scenario}
               </Text>
               <Text size={400} className={classes.text} weight={'bold'}>
                  {risk?.protectedAsset?.name}
               </Text>
            </div>
         </>
      )
   }

   useEffect(() => {
      if (analysis.simplifiedRisks.length > 0) {
         setRisks(analysis.simplifiedRisks)
      } else if (analysis.consequenceAndProbabilityRisks.length > 0) {
         setRisks(analysis.consequenceAndProbabilityRisks)
      }
   }, [analysis])

   const title = t('RiskIdentifying')
   return (
      <>
         <CardHeader
            title={title}
            action={
               <Button className={classes.btn} onClick={handleNewClick} appearance="primary" icon={<AddCircle24Regular />} disabled={disabled}>
                  {t('NewRisk')}
               </Button>
            }
         />
         {risks.length === 0 ? (
            <InfoMessageBar title={t('TimeToCreateYourFirstItem', { Item: t('Risk').toLowerCase() })} />
         ) : (
            <List columns={getSimplifiedRiskIdentifyingColumns()} items={risks} onRowClick={handleRowClick} renderPhoneBodyItem={phoneBody} />
         )}

         <ModalRisk
            setRiskAnalysisState={setRiskAnalysisState}
            analysis={analysis}
            open={openModal}
            setOpen={setOpenModal}
            selectedItem={selectedItem}
            disabled={disabled || selectedItem?.determined}
            lastUsedAssetGoalId={lastUsedAssetGoalId}
            setLastUsedAssetGoalId={setLastUsedAssetGoalId}
            lastUsedThreatId={lastUsedThreatId}
            setLastUsedThreatId={setLastUsedThreatId}
         />

         <ModuleGuard allowedModules={[RiskManagementModules.SIMPLIFIED_RISK_ANALYSIS, RiskManagementModules.CONSEQUENCE_AND_PROBABILITY_ANALYSIS]}>
            <ModalWarningNoProtectedAssetGoal open={openWarningModal} close={handleCloseWarningModal} />
         </ModuleGuard>
      </>
   )
}
